/**
 * The email address on the account, and the second lock on it.
 *
 * Both live in Settings and both are about the same thing: being able to
 * prove the account is yours when the password alone is not enough - or is no
 * longer a secret.
 */

import { useEffect, useState } from 'react';
import { api, endpoints } from '@/lib/api';
import { useSession } from '@/stores/session';
import { copyText } from '@/lib/clipboard';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { Button, Card, CardHead, Field, Input, Tag, useConfirm, useToast } from './ui';
import { CodeInput } from '@/views/AuthView';

type State = {
  enabled: boolean;
  method: 'app' | 'email' | null;
  recoveryLeft: number;
  canUseEmail: boolean;
  since: number | null;
};

/* ------------------------------------------------------------- email */

export function EmailCard() {
  const me = useSession((s) => s.me);
  const refresh = useSession((s) => s.refresh);
  const toast = useToast();

  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);
  const [next, setNext] = useState('');
  const [password, setPassword] = useState('');
  const [cooldown, setCooldown] = useState(0);

  // Counts down so the resend button says when, rather than just refusing.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (!me) return null;
  const verified = !!me.emailVerified;

  async function verify() {
    setBusy(true);
    try {
      await endpoints.verifyEmail(code.trim());
      await refresh();
      toast('Email confirmed', 'good');
      setCode('');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    try {
      const res = await endpoints.resendVerify();
      toast('Code sent to ' + res.sent, 'good');
      setCooldown(60);
    } catch (err) {
      const failure = err as Error & { wait?: number };
      if (failure.wait) setCooldown(failure.wait);
      toast(failure.message, 'bad');
    }
  }

  async function change() {
    setBusy(true);
    try {
      const res = await endpoints.changeEmail(next.trim(), password);
      await refresh();
      toast('Code sent to ' + res.sent, 'good');
      setChanging(false);
      setNext('');
      setPassword('');
      setCooldown(60);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHead
        title="Email"
        note="Where password resets and sign-in codes go"
        actions={verified
          ? <Tag kind="good">CONFIRMED</Tag>
          : <Tag kind="warn">NOT CONFIRMED</Tag>}
      />

      <div className="flex flex-wrap items-center gap-3">
        <Icon name={verified ? 'checkCirc' : 'alert'} size={16} className={verified ? 'text-good' : 'text-warn'} />
        <span className="selectable min-w-0 flex-1 truncate text-[13.5px] font-semibold">
          {me.email || 'No address on this account'}
        </span>
        <Button kind="outline" size="sm" onClick={() => setChanging((v) => !v)}>
          {changing ? 'Cancel' : 'Change'}
        </Button>
      </div>

      {/* Not confirmed yet: the code goes here. */}
      {!verified && me.email && !changing ? (
        <div className="mt-[var(--gap)] rounded-[var(--radius)] border border-warn bg-[color-mix(in_srgb,var(--warn)_9%,transparent)] p-3.5">
          <p className="text-[12.5px] text-dim">
            Type the code we sent to confirm this address. Until you do, there is no way to reset
            your password.
          </p>
          <div className="mt-2.5 flex flex-wrap items-end gap-2">
            <div className="min-w-[180px] flex-1">
              <CodeInput value={code} onChange={setCode} autoFocus={false} />
            </div>
            <Button kind="primary" disabled={busy || code.trim().length < 6} onClick={() => void verify()}>
              Confirm
            </Button>
            <Button kind="ghost" disabled={cooldown > 0} onClick={() => void resend()}>
              {cooldown > 0 ? 'Resend in ' + cooldown + 's' : 'Resend'}
            </Button>
          </div>
        </div>
      ) : null}

      {/* Changing it takes the password, and unconfirms the new one. */}
      {changing ? (
        <div className="mt-[var(--gap)] grid gap-[var(--gap)] sm:grid-cols-2">
          <Field label="New address">
            <Input
              type="email"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="you@gmail.com"
              spellCheck={false}
            />
          </Field>
          <Field label="Your password" hint="So nobody who borrows your screen can move it.">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </Field>
          <div className="sm:col-span-2">
            <Button kind="primary" disabled={busy || !next || !password} onClick={() => void change()}>
              {busy ? 'Sending…' : 'Send a code to the new address'}
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

/* -------------------------------------------------------- two-factor */

export function TwoFactorCard() {
  const toast = useToast();
  const { confirm, dialog } = useConfirm();

  const [state, setState] = useState<State | null>(null);
  const [setup, setSetup] = useState<{ method: string; secret?: string; uri?: string; qr?: string; sent?: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.get<State>('/api/auth/2fa').then(setState).catch(() => setState(null));
  useEffect(() => { void load(); }, []);

  async function begin(method: 'app' | 'email') {
    setBusy(true);
    try {
      setSetup(await api.post('/api/auth/2fa/setup', { method }));
      setCode('');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function enable() {
    setBusy(true);
    try {
      const res = await api.post<{ recovery: string[] }>('/api/auth/2fa/enable', { code: code.trim() });
      setCodes(res.recovery);
      setSetup(null);
      setCode('');
      await load();
      toast('Two-factor is on', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    const ok = await confirm(
      'Turn two-factor off?',
      'Your account goes back to being protected by the password alone.',
      { confirmLabel: 'Turn it off', danger: true }
    );
    if (!ok) return;
    try {
      await api.post('/api/auth/2fa/disable', { password });
      setPassword('');
      await load();
      toast('Two-factor is off', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  async function regenerate() {
    try {
      const res = await api.post<{ recovery: string[] }>('/api/auth/2fa/recovery', { password });
      setCodes(res.recovery);
      setPassword('');
      await load();
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  if (!state) return null;

  return (
    <Card>
      <CardHead
        title="Two-factor authentication"
        note="A code as well as your password, every time you sign in"
        actions={state.enabled ? <Tag kind="good">ON</Tag> : <Tag>OFF</Tag>}
      />

      {/* ---- the codes, shown exactly once ---- */}
      {codes ? (
        <div className="rounded-[var(--radius)] border border-accent-line bg-accent-soft p-4">
          <p className="text-[13px] font-bold text-accent-hi">Save these somewhere safe</p>
          <p className="mt-1 text-[12.5px] text-dim">
            Each one signs you in once if you lose your phone. This is the only time they are
            shown — we keep them hashed, so we cannot show them again.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            {codes.map((c) => (
              <code key={c} className="selectable rounded-[calc(8px*var(--radius-mul,1))] bg-surface-0 px-2 py-1.5 text-center font-mono text-[12.5px]">
                {c}
              </code>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              kind="outline"
              size="sm"
              onClick={() => { void copyText(codes.join('\n')); toast('Copied', 'good'); }}
            >
              Copy all
            </Button>
            <Button kind="primary" size="sm" onClick={() => setCodes(null)}>I have saved them</Button>
          </div>
        </div>
      ) : null}

      {/* ---- off, and not yet being turned on ---- */}
      {!state.enabled && !setup && !codes ? (
        <div className="flex flex-col gap-2.5">
          <Choice
            icon="lock"
            title="Authenticator app"
            note="Google Authenticator, Aegis, 1Password — anything that scans a QR. Works with no signal."
            action={<Button kind="primary" size="sm" disabled={busy} onClick={() => void begin('app')}>Set up</Button>}
          />
          <Choice
            icon="inbox"
            title="Email a code"
            note={state.canUseEmail
              ? 'A code goes to your inbox each time you sign in.'
              : 'Confirm your email address first.'}
            action={
              <Button kind="outline" size="sm" disabled={busy || !state.canUseEmail} onClick={() => void begin('email')}>
                Set up
              </Button>
            }
          />
        </div>
      ) : null}

      {/* ---- mid-setup ---- */}
      {setup ? (
        <div className="flex flex-col gap-[var(--gap)]">
          {setup.method === 'app' ? (
            <div className="flex flex-wrap items-start gap-5">
              {setup.qr ? (
                // The server draws it; a white plate because a QR needs the
                // contrast and the app is dark everywhere else.
                <div
                  className="h-[168px] w-[168px] flex-none rounded-[calc(10px*var(--radius-mul,1))] bg-white p-2.5 [&_svg]:h-full [&_svg]:w-full"
                  dangerouslySetInnerHTML={{ __html: setup.qr }}
                />
              ) : null}
              <div className="min-w-[220px] flex-1">
                <p className="text-[13px] text-dim">Scan this with your authenticator app.</p>
                <p className="mt-2 text-[11.5px] text-mute">Cannot scan? Type this key in instead:</p>
                <code className="selectable mt-1 block rounded-[calc(8px*var(--radius-mul,1))] border border-line-soft bg-surface-1 px-2.5 py-2 font-mono text-[12.5px] break-all">
                  {setup.secret}
                </code>
                <Button
                  kind="ghost"
                  size="sm"
                  className="mt-1.5"
                  onClick={() => { void copyText(setup.secret ?? ''); toast('Copied', 'good'); }}
                >
                  Copy key
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-[13px] text-dim">We sent a code to {setup.sent}. Type it below to finish.</p>
          )}

          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[180px] flex-1">
              <span className="mb-1 block text-[10.5px] font-bold tracking-[.07em] text-mute uppercase">
                Code
              </span>
              <CodeInput value={code} onChange={setCode} autoFocus={false} />
            </div>
            <Button kind="primary" disabled={busy || code.trim().length < 6} onClick={() => void enable()}>
              Turn it on
            </Button>
            <Button kind="ghost" onClick={() => { setSetup(null); setCode(''); }}>Cancel</Button>
          </div>
        </div>
      ) : null}

      {/* ---- on ---- */}
      {state.enabled && !codes ? (
        <div className="flex flex-col gap-[var(--gap)]">
          <div className="flex flex-wrap items-center gap-3 rounded-[var(--radius)] border border-line-soft bg-surface-1 p-3.5">
            <Icon name="checkCirc" size={16} className="text-good" />
            <span className="min-w-0 flex-1 text-[13px]">
              Protected by {state.method === 'email' ? 'a code sent to your email' : 'your authenticator app'}.
              <span className="block text-[12px] text-mute">
                {state.recoveryLeft} recovery {state.recoveryLeft === 1 ? 'code' : 'codes'} left.
              </span>
            </span>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <Field label="Your password" hint="Needed to change either of these.">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-[220px]"
              />
            </Field>
            <Button kind="outline" disabled={!password} onClick={() => void regenerate()}>
              New recovery codes
            </Button>
            <Button kind="danger" disabled={!password} onClick={() => void disable()}>
              Turn off
            </Button>
          </div>
        </div>
      ) : null}

      {dialog}
    </Card>
  );
}

function Choice({ icon, title, note, action }: {
  icon: string; title: string; note: string; action: React.ReactNode;
}) {
  return (
    <div className={cn(
      'flex flex-wrap items-center gap-3 rounded-[var(--radius)] border border-line-soft bg-surface-1 p-3.5'
    )}>
      <span className="grid h-9 w-9 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] bg-surface-3 text-accent-hi">
        <Icon name={icon} size={16} />
      </span>
      <span className="min-w-[180px] flex-1">
        <span className="block text-[13.5px] font-semibold">{title}</span>
        <span className="block text-[12px] text-mute">{note}</span>
      </span>
      {action}
    </div>
  );
}
